import React from "react";
import PropTypes from "prop-types";
import { Link, useLocation } from "react-router-dom";
import EmptyValue from "../../components/EmptyValue";

export { formatCurrency } from "../../utils/format";

// Four read-heavy finance views over the SAME order/payment data the pharmacist console
// already serves (`.scratch/finance-dashboard/IMPLEMENTATION.md` §1/§5.5) — real routes
// via `Link`, mirroring the procurement console's `ProcurementTabs`
// (../procurement/shared.js), not local-state tab switching.
export const TABS = [
    { key: "orders", label: "Orders", path: "/v2/finance/orders" },
    { key: "quotations", label: "Quotations", path: "/v2/finance/quotations" },
    { key: "refunds", label: "Refunds", path: "/v2/finance/refunds" },
    { key: "cod", label: "COD", path: "/v2/finance/cod" },
];

export function FinanceTabs() {
    const location = useLocation();
    return (
        <nav className="mt-4 flex gap-1 border-b border-[var(--ops-carbon-200)]" aria-label="Finance views">
            {TABS.map((tab) => {
                const active = location.pathname === tab.path;
                return (
                    <Link
                        key={tab.key}
                        to={tab.path}
                        className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${
                            active
                                ? "border-[var(--ops-emerald-500)] text-[var(--ops-emerald-600)]"
                                : "border-transparent text-[var(--ops-carbon-500)] hover:text-[var(--ops-carbon-900)]"
                        }`}
                    >
                        {tab.label}
                    </Link>
                );
            })}
        </nav>
    );
}

// Orders/Quotations column 3 — "patient via userOrders.userMappingId -> mapUserPatients;
// phone as subtext" (§2/§3). The Patient profile modal the prototype opens from this cell
// is explicitly out of scope until specified (§6) — this renders plain text, not a link.
export function NamePhoneCell({ mapUserPatient }) {
    const patient = mapUserPatient?.Patient;
    if (!patient?.name && !patient?.contact_no) return <EmptyValue />;
    return (
        <div>
            <div className="font-medium text-[var(--ops-carbon-900)]">{patient.name || <EmptyValue />}</div>
            {patient.contact_no && <div className="text-xs text-[var(--ops-carbon-500)]">{patient.contact_no}</div>}
        </div>
    );
}
NamePhoneCell.propTypes = {
    mapUserPatient: PropTypes.shape({
        Patient: PropTypes.shape({
            name: PropTypes.string,
            contact_no: PropTypes.string,
        }),
    }),
};

// Orders columns 10-13 (Payment Gateway/Method/Source/Status) — §2's LOCKED decision:
// render EVERY payment, comma-separated, index-aligned, NOT deduplicated (two ₹500 UPI
// payments are two real events; collapsing them would hide one). "—" for a null field so
// alignment survives a payment missing that one attribute.
export function joinPaymentField(payments, field) {
    if (!payments?.length) return <EmptyValue />;
    return payments.map((p) => p[field] || "—").join(", ");
}

// Refunds/COD — "—" for a value that never resolved (orphaned map data, missing join
// side), consistent with EmptyValue's convention everywhere else in this console.
export function textOrEmpty(value) {
    return value || value === 0 ? value : <EmptyValue />;
}
